import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';

const q = async (p) => { const { data, error } = await p; if (error) throw new Error(error.message); return data; };

// Header aliases → bl_import row keys. Cost/rate columns are deliberately ignored (no € in the Build-Up Library).
const ALIASES = {
  code: ['alvora code', 'code', 'build-up code', 'buildup code'],
  name: ['build-up name', 'buildup name', 'name'],
  trade: ['category / trade', 'trade', 'category/trade'],
  category: ['category', 'sub-category', 'element'],
  primary_unit: ['primary unit', 'unit', 'measurement unit'],
  specification: ['starter specification / variant', 'specification', 'specification / variant', 'description'],
  operations_inline: ['included operations (starter scope)', 'included operations', 'operations', 'operations_inline'],
  scope: ['scope'],
  project_code: ['project code', 'project'],
  technical_reference: ['source / approved detail', 'technical reference', 'technical_reference'],
  review_note: ['technical review', 'review note', 'review_note'],
  review_status: ['review status', 'review_status'],
  source_document_ref: ['source document', 'source document ref', 'source_document_ref'],
  drawing_number: ['drawing number', 'drawing no', 'drawing_number'],
  drawing_revision: ['drawing revision', 'revision', 'drawing_revision'],
  detail_reference: ['detail reference', 'detail ref', 'detail_reference'],
  source_description: ['source description', 'source_description'],
};
const IGNORED = ['labour hours / unit', 'labour cost / unit (€)', 'labour cost / unit', 'selling rate', 'cost / unit', 'status'];
const norm = (h) => String(h ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function mapHeaders(headers) {
  const map = {}; const ignored = []; const unknown = [];
  headers.forEach((h, i) => {
    const n = norm(h); if (!n) return;
    const key = Object.keys(ALIASES).find((k) => ALIASES[k].includes(n));
    if (key) map[key] = i; else if (IGNORED.includes(n)) ignored.push(String(h)); else unknown.push(String(h));
  });
  return { map, ignored, unknown };
}

// Parses sheet 1 of an XLSX/CSV into bl_import rows. Child sheets (Operations/Attributes/Units/Resources) are attached by code when present.
export async function parseWorkbook(file) {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const main = wb.Sheets[wb.SheetNames[0]];
  const grid = XLSX.utils.sheet_to_json(main, { header: 1, raw: false, defval: '' });
  const headers = grid[0] || [];
  const { map, ignored, unknown } = mapHeaders(headers);
  const missing = ['code', 'name', 'trade', 'primary_unit'].filter((k) => map[k] === undefined);
  const rows = grid.slice(1).map((r, idx) => {
    if (!r.some((c) => String(c).trim() !== '')) return null;
    const row = { row_no: idx + 2 };
    for (const [k, i] of Object.entries(map)) { const v = String(r[i] ?? '').trim(); if (v) row[k] = v; }
    return row;
  }).filter(Boolean);
  const children = {};
  for (const [sheetKey, listKey] of [['operations', 'operations'], ['attributes', 'attributes'], ['units', 'units'], ['resources', 'resources']]) {
    const sh = wb.SheetNames.find((n) => n.toLowerCase() === sheetKey);
    if (!sh) continue;
    for (const rec of XLSX.utils.sheet_to_json(wb.Sheets[sh], { raw: false, defval: '' })) {
      const obj = Object.fromEntries(Object.entries(rec).map(([k, v]) => [norm(k).replace(/ /g, '_'), String(v).trim()]).filter(([, v]) => v));
      const code = obj.code; if (!code) continue;
      (children[code] ||= {})[listKey] ||= []; children[code][listKey].push(Object.fromEntries(Object.entries(obj).filter(([k]) => k !== 'code')));
    }
  }
  rows.forEach((row) => { if (children[row.code]) Object.assign(row, children[row.code]); });
  return { rows, headers, map, ignored, unknown, missing, sheetName: wb.SheetNames[0], childSheets: Object.keys(children).length ? wb.SheetNames.slice(1) : [] };
}

export const runImport = (org, fileName, rows, commit, ignored) => q(supabase.rpc('bl_import', { _org: org, _file_name: fileName, _rows: rows, _commit: commit, _ignored_columns: ignored?.length ? ignored : null }));
export const batchRows = (batchId) => q(supabase.from('buildup_import_rows').select('row_no, code, result, message').eq('batch_id', batchId).order('row_no'));
export const recentBatches = (org) => q(supabase.from('buildup_import_batches').select('*').eq('organisation_id', org).order('created_at', { ascending: false }).limit(10));
export const libraryCounts = (org) => q(supabase.from('buildup_templates').select('status').eq('organisation_id', org));
